import type { ReactNode } from 'react'
import { Fragment } from 'react'
import { useT } from './index'

/**
 * Translate a key whose placeholders must be filled with JSX rather
 * than plain text — a styled span, a link, a coloured phrase.
 *
 * The alternative, splitting the sentence into fragments around the
 * values ({t('How do you')} {a} {t('while')} {b} …), silently hardcodes
 * ENGLISH word order: every fragment renders in the order it was
 * written, so a language that moves the verb (Japanese puts it last,
 * German sends the infinitive to the end) can never come out right, and
 * a lone fragment like 'while' is unresolvable for a translator anyway.
 *
 * Here the whole sentence is ONE key, so each language may reorder it
 * freely, and the placeholders find their values wherever they land.
 *
 *   tn('How do you {a} while {b}?', {
 *     a: <span className="text-hot">{actionA}</span>,
 *     b: <span className="text-hot">{actionB}</span>,
 *   })
 */
export function useTNodes() {
  const t = useT()
  return function tn(key: string, nodes: Record<string, ReactNode>): ReactNode[] {
    const out: ReactNode[] = []
    // Split on {placeholder}; odd indices are the placeholder names.
    const parts = t(key).split(/\{(\w+)\}/g)
    parts.forEach((part, i) => {
      if (i % 2 === 1) out.push(<Fragment key={i}>{nodes[part] ?? `{${part}}`}</Fragment>)
      else if (part) out.push(<Fragment key={i}>{part}</Fragment>)
    })
    return out
  }
}
